/**
 * Header v2 — responsive mega menu + drawer web component.
 *
 * Handles:
 *  - Desktop: hover-to-open mega menu (click still works, for keyboard/touch),
 *    click-outside / ESC to close, sub-panel hover/click
 *  - Mobile/tablet: drawer with drill-down navigation (root → L2 → L3)
 *  - Focus trap inside drawer, scroll lock, focus restore on close
 *  - Breakpoint switch: closes any open panel
 */

class HeaderV2 extends HTMLElement {
  constructor() {
    super();
    this.activeMegaPanel = null;
    this.lastFocused = null;
    this.bodyScrollY = 0;
    this.desktopMQ = window.matchMedia('(min-width: 1025px)');
    // Bridges the gap between leaving a nav item and arriving at its panel —
    // they aren't DOM neighbors, so a plain mouseleave would close the menu
    // while the pointer is still travelling down into it.
    this.megaCloseTimer = null;

    this.stickyType = 'none';
    this.stickyShowThreshold = 100;
    this.stickyHideOffset = 80;
    this.stickyAccumulator = 0;
    this.lastScrollY = 0;
    this.stickyHidden = false;
    this.stickyTicking = false;
    this.scrollMode = 'flow';
    this.isRevealed = false;
    this.spacer = null;
    this.wrapperOffsetTop = 0;

    this.cartUpdateUnsubscriber = undefined;

    this.onDocClick = this.onDocClick.bind(this);
    this.onDocKey = this.onDocKey.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.trapFocusHandler = this.trapFocusHandler.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  connectedCallback() {
    this.cacheNodes();
    this.activeDrawerPanel = 'root';
    this.stickyType = this.dataset.sticky || 'none';
    this.isTransparent = this.dataset.transparent === 'true';
    this.lastScrollY = window.scrollY;
    this.outerWrapper = this.closest('.shopify-section') || this.parentElement;
    if (this.outerWrapper && this.stickyType !== 'none') {
      this.outerWrapper.classList.add('section-header-v2--sticky');
      if (this.stickyType === 'on-scroll-up') {
        this.outerWrapper.classList.add('section-header-v2--sticky-on-scroll-up');
      } else if (this.stickyType === 'always') {
        this.outerWrapper.classList.add('section-header-v2--sticky-always');
      }
    }
    // Compute wrapper offsetTop after layout settles. Used as the "exit
    // fixed-mode" threshold so the swap back to in-flow happens exactly
    // when natural position == viewport top (zero jump).
    if (this.outerWrapper && this.stickyType === 'on-scroll-up') {
      requestAnimationFrame(() => {
        this.wrapperOffsetTop = this.outerWrapper.offsetTop;
        // If page loaded already past the wrapper, enter fixed-mode silently.
        if (this.outerWrapper.getBoundingClientRect().bottom <= 0) {
          this.enterFixedMode();
        }
      });
    }
    this.bindEvents();
    if (this.isTransparent) this.updateScrolled();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.updateCartCount(event.cartData.item_count);
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKey);
    window.removeEventListener('scroll', this.onScroll);
    if (this.desktopMQ && this.desktopMQ.removeEventListener) {
      this.desktopMQ.removeEventListener('change', this.onBreakpointChange);
    }
    this.cancelMegaClose();
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    // Remove the body-reparented overlay/drawer so theme-editor reloads
    // don't leak duplicates.
    if (this.megaOverlay && this.megaOverlay.parentElement === document.body) {
      this.megaOverlay.remove();
    }
    if (this.drawer && this.drawer.parentElement === document.body) {
      this.drawer.remove();
    }
  }

  cacheNodes() {
    this.megaContainer = this.querySelector('[data-header-v2-mega]');
    this.megaOverlay = this.querySelector('.header-v2__mega-overlay');
    this.drawer = this.querySelector('[data-header-v2-drawer]');
    this.cartLinkEl = this.querySelector('.header-v2__cart-link');

    // Reparent overlay to <body> so position:fixed isn't trapped inside any
    // ancestor that creates a containing block (sticky wrapper, transforms,
    // backdrop-filter, etc.). This is what was hiding the dim from view.
    if (this.megaOverlay && this.megaOverlay.parentElement !== document.body) {
      document.body.appendChild(this.megaOverlay);
    }
    // Same reason for the drawer: when the header element gets a transform
    // in fixed-mode, it becomes a containing block for fixed descendants.
    // The drawer's `position: fixed; inset: 0` would then be trapped inside
    // the 62px header box instead of filling the viewport.
    if (this.drawer && this.drawer.parentElement !== document.body) {
      document.body.appendChild(this.drawer);
    }

    this.megaTogglers = this.querySelectorAll('[data-header-v2-mega-toggle]');
    this.megaPanels = this.querySelectorAll('[data-mega-panel]');
    this.megaSubTogglers = this.querySelectorAll('[data-mega-sub-toggle]');
    this.megaSubPanels = this.querySelectorAll('[data-mega-sub-panel]');
    this.megaSubClearers = this.querySelectorAll('[data-mega-sub-clear]');
    // Overlay was reparented to <body>, so query its close-trigger from document.
    this.megaClosers = document.querySelectorAll('[data-header-v2-mega-close]');

    // Toggle (hamburger) lives inside <header-v2>, so query it from `this`.
    this.drawerTogglers = this.querySelectorAll('[data-header-v2-drawer-toggle]');
    // Drawer was reparented to <body>; its inner elements must be queried
    // from the drawer itself, not from `this`.
    const drawerScope = this.drawer || this;
    this.drawerClosers = drawerScope.querySelectorAll('[data-header-v2-drawer-close]');
    this.drawerDrillers = drawerScope.querySelectorAll('[data-drawer-drill]');
    this.drawerBackers = drawerScope.querySelectorAll('[data-drawer-back]');
    this.drawerPanels = drawerScope.querySelectorAll('[data-drawer-panel]');
  }

  bindEvents() {
    this.megaTogglers.forEach((btn) => {
      btn.addEventListener('click', (e) => this.onMegaToggleClick(e));
      // Desktop only (see onMegaToggleEnter / scheduleMegaClose) — mobile and
      // tablet keep the click-driven drawer, untouched.
      btn.addEventListener('mouseenter', (e) => this.onMegaToggleEnter(e));
      btn.addEventListener('mouseleave', () => this.scheduleMegaClose());
    });
    this.megaClosers.forEach((el) => el.addEventListener('click', () => this.closeMega()));

    if (this.megaContainer) {
      this.megaContainer.addEventListener('mouseenter', () => this.cancelMegaClose());
      this.megaContainer.addEventListener('mouseleave', () => this.scheduleMegaClose());
    }

    this.megaSubTogglers.forEach((btn) => {
      btn.addEventListener('click', (e) => this.onMegaSubClick(e));
      btn.addEventListener('mouseenter', (e) => this.onMegaSubClick(e));
      btn.addEventListener('focus', (e) => this.onMegaSubClick(e));
    });

    this.megaSubClearers.forEach((el) => {
      el.addEventListener('mouseenter', (e) => this.onMegaSubClear(e));
      el.addEventListener('focus', (e) => this.onMegaSubClear(e));
    });

    this.drawerTogglers.forEach((btn) => btn.addEventListener('click', (e) => this.onDrawerToggle(e)));
    this.drawerClosers.forEach((btn) => btn.addEventListener('click', (e) => this.onDrawerClose(e)));
    this.drawerDrillers.forEach((btn) => btn.addEventListener('click', (e) => this.onDrawerDrill(e)));
    this.drawerBackers.forEach((btn) => btn.addEventListener('click', (e) => this.onDrawerBack(e)));

    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onDocKey);

    if (this.desktopMQ && this.desktopMQ.addEventListener) {
      this.desktopMQ.addEventListener('change', this.onBreakpointChange);
    }

    if (this.stickyType === 'on-scroll-up' || this.isTransparent) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  /* ===== Sticky hide/show ===== */

  onScroll() {
    if (this.stickyTicking) return;
    this.stickyTicking = true;
    requestAnimationFrame(() => {
      if (this.stickyType === 'on-scroll-up') this.updateSticky();
      if (this.isTransparent) this.updateScrolled();
      this.stickyTicking = false;
    });
  }

  updateScrolled() {
    // on-scroll-up: bg color is driven by reveal state (set inside showRevealed).
    if (this.stickyType === 'on-scroll-up') return;
    this.classList.toggle('is-scrolled', window.scrollY > 0);
  }

  /* on-scroll-up flow:
     - At top: wrapper in normal flow, scrolls away naturally with the page
     - Wrapper fully above viewport: enter fixed-mode (translateY -100%)
     - Scroll up by 100px accumulated: .is-revealed slides it in
     - Scroll down 80px+ in fixed-mode: hide via .is-revealed removal
     - Scroll up to wrapperOffsetTop: exit fixed-mode (zero jump because
       wrapper's natural position == viewport top at this scrollY) */
  updateSticky() {
    if (this.classList.contains('is-mega-open') || this.classList.contains('is-drawer-open')) {
      this.lastScrollY = window.scrollY;
      return;
    }

    const currentY = window.scrollY;
    const delta = currentY - this.lastScrollY;

    if (currentY <= this.wrapperOffsetTop) {
      this.exitFixedMode();
      this.stickyAccumulator = 0;
      this.lastScrollY = currentY;
      return;
    }

    if (this.scrollMode === 'flow') {
      if (
        delta > 0 &&
        this.outerWrapper &&
        this.outerWrapper.getBoundingClientRect().bottom <= 0
      ) {
        this.enterFixedMode();
      }
    } else {
      if (delta > 2) {
        this.stickyAccumulator = 0;
        if (this.isRevealed) this.hideRevealed();
      } else if (delta < -2) {
        this.stickyAccumulator += -delta;
        if (this.stickyAccumulator >= this.stickyShowThreshold) {
          this.showRevealed();
        }
      }
    }

    this.lastScrollY = currentY;
  }

  enterFixedMode() {
    if (this.scrollMode === 'fixed') return;
    if (!this.outerWrapper) return;

    const wrapper = this.outerWrapper;
    // Cache offsetTop while wrapper is still in flow (used by exit threshold)
    this.wrapperOffsetTop = wrapper.offsetTop;
    // Lock wrapper to its current rendered height. The inner <header-v2>
    // is about to go position:fixed (out of flow within the wrapper); the
    // locked height keeps the wrapper itself the same size in the page
    // layout, so nothing below moves and there's no jerk.
    wrapper.style.height = `${wrapper.offsetHeight}px`;

    this.scrollMode = 'fixed';
    // Suppress transition on header-v2 for the silent in-flow → fixed swap
    // (otherwise it would animate from translateY(0) to translateY(-100%)).
    this.style.transition = 'none';
    wrapper.classList.add('is-fixed-mode');
    void this.offsetHeight;
    this.style.transition = '';
  }

  exitFixedMode() {
    if (this.scrollMode === 'flow') return;
    this.scrollMode = 'flow';

    if (this.outerWrapper) {
      this.style.transition = 'none';
      this.outerWrapper.classList.remove('is-fixed-mode', 'is-revealed');
      this.outerWrapper.style.height = '';
      void this.offsetHeight;
      this.style.transition = '';
    }
    this.isRevealed = false;
    this.classList.remove('is-scrolled');
  }

  showRevealed() {
    if (this.isRevealed) return;
    this.isRevealed = true;
    (this.outerWrapper || this).classList.add('is-revealed');
    this.classList.add('is-scrolled');
  }

  hideRevealed() {
    if (!this.isRevealed) return;
    this.isRevealed = false;
    (this.outerWrapper || this).classList.remove('is-revealed');
    this.classList.remove('is-scrolled');
  }

  hideSticky() {
    if (this.stickyHidden) return;
    (this.outerWrapper || this).classList.add('is-sticky-hidden');
    this.stickyHidden = true;
  }

  showSticky() {
    // on-scroll-up uses fixed-mode/revealed instead of is-sticky-hidden.
    // openMega/openDrawer "snap back" routes through showRevealed.
    if (this.stickyType === 'on-scroll-up') {
      if (this.scrollMode === 'fixed') this.showRevealed();
      return;
    }
    if (!this.stickyHidden) return;
    (this.outerWrapper || this).classList.remove('is-sticky-hidden');
    this.stickyHidden = false;
  }

  /* Public: force the header into view (e.g. after add-to-cart) so any
     message anchored to it is visible regardless of current scroll state. */
  reveal() {
    if (this.stickyType === 'on-scroll-up') {
      // If scrolled past the header, pin it and slide it in.
      if (window.scrollY > this.wrapperOffsetTop) {
        this.enterFixedMode();
        this.stickyAccumulator = 0;
        this.showRevealed();
        return;
      }
    }

    if (this.stickyType === 'always') {
      this.showSticky();
      return;
    }

    // Non-sticky (or still near the top): scroll the page up so the
    // header — and the message under it — is on screen.
    if (window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /* ===== Mega Menu ===== */

  onMegaToggleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;
    const index = btn.dataset.headerV2MegaToggle;
    if (!index) return;

    if (this.activeMegaPanel === index) {
      this.closeMega();
    } else {
      this.openMega(index);
    }
  }

  // Desktop hover-open. Keyboard and touch still go through the click handler
  // above (a focused button's Enter/Space dispatches a click natively), so
  // this only changes how a mouse user reaches the panel.
  onMegaToggleEnter(e) {
    if (!this.desktopMQ.matches) return;
    this.cancelMegaClose();
    const index = e.currentTarget.dataset.headerV2MegaToggle;
    if (!index || this.activeMegaPanel === index) return;
    this.openMega(index);
  }

  // Closes after a short delay rather than immediately, so moving the pointer
  // from the nav item down into the panel — crossing empty space, since the
  // two aren't DOM neighbors — doesn't read as "left the menu".
  scheduleMegaClose() {
    if (!this.desktopMQ.matches) return;
    this.cancelMegaClose();
    this.megaCloseTimer = setTimeout(() => {
      this.megaCloseTimer = null;
      this.closeMega();
    }, 200);
  }

  cancelMegaClose() {
    if (this.megaCloseTimer) {
      clearTimeout(this.megaCloseTimer);
      this.megaCloseTimer = null;
    }
  }

  openMega(index) {
    if (!this.megaContainer) return;

    // If sticky-hide had translated the header off-screen, snap it back so
    // the panel doesn't open under an invisible header.
    this.showSticky();

    const wasClosed = this.activeMegaPanel == null;
    this.megaContainer.hidden = false;
    if (this.megaOverlay) this.megaOverlay.hidden = false;

    this.megaPanels.forEach((panel) => {
      const match = panel.dataset.megaPanel === index;
      panel.hidden = !match;
    });

    this.megaTogglers.forEach((btn) => {
      const match = btn.dataset.headerV2MegaToggle === index;
      btn.setAttribute('aria-expanded', match ? 'true' : 'false');
      btn.classList.toggle('is-open', match);
    });

    if (wasClosed) {
      this.megaScrollY = window.scrollY;
      document.body.classList.add('is-header-v2-mega-open');
      document.body.style.top = `-${this.megaScrollY}px`;
    }

    requestAnimationFrame(() => {
      this.classList.add('is-mega-open');
      this.megaContainer.classList.add('is-open');
    });

    this.activeMegaPanel = index;
  }

  closeMega() {
    this.cancelMegaClose();
    if (!this.megaContainer || this.activeMegaPanel == null) return;

    this.classList.remove('is-mega-open');
    this.megaContainer.classList.remove('is-open');

    document.body.classList.remove('is-header-v2-mega-open');
    document.body.style.top = '';
    if (this.megaScrollY != null) {
      this.lastScrollY = this.megaScrollY;
      window.scrollTo(0, this.megaScrollY);
      this.megaScrollY = null;
    }

    setTimeout(() => {
      if (this.activeMegaPanel == null) {
        this.megaContainer.hidden = true;
        if (this.megaOverlay) this.megaOverlay.hidden = true;
        this.megaPanels.forEach((panel) => {
          panel.hidden = true;
        });
      }
    }, 350);

    this.megaTogglers.forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      btn.classList.remove('is-open');
    });

    this.activeMegaPanel = null;
  }

  onMegaSubClick(e) {
    const btn = e.currentTarget;
    const key = btn.dataset.megaSubToggle;
    if (!key) return;
    const [panelIdx] = key.split('-');

    this.querySelectorAll(`[data-mega-sub-toggle^="${panelIdx}-"]`).forEach((b) => {
      const match = b === btn;
      b.classList.toggle('is-active', match);
      b.setAttribute('aria-expanded', match ? 'true' : 'false');
    });

    this.querySelectorAll(`[data-mega-sub-panel^="${panelIdx}-"]`).forEach((p) => {
      p.classList.toggle('is-active', p.dataset.megaSubPanel === key);
    });
  }

  onMegaSubClear(e) {
    const el = e.currentTarget;
    const panelIdx = el.dataset.megaSubClear;
    if (!panelIdx) return;

    this.querySelectorAll(`[data-mega-sub-toggle^="${panelIdx}-"]`).forEach((b) => {
      b.classList.remove('is-active');
      b.setAttribute('aria-expanded', 'false');
    });

    this.querySelectorAll(`[data-mega-sub-panel^="${panelIdx}-"]`).forEach((p) => {
      p.classList.remove('is-active');
    });
  }

  /* ===== Drawer ===== */

  onDrawerToggle(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.classList.contains('is-drawer-open')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    if (!this.drawer) return;

    // If sticky-hide had translated the header off-screen, snap it back so
    // the drawer doesn't open under an invisible header (visible on transparent
    // headers in tablet/mobile after scrolling down).
    this.showSticky();

    this.lastFocused = document.activeElement;
    this.bodyScrollY = window.scrollY;

    document.body.classList.add('is-header-v2-drawer-open');
    document.body.style.top = `-${this.bodyScrollY}px`;

    this.drawer.hidden = false;

    requestAnimationFrame(() => {
      this.classList.add('is-drawer-open');
      this.drawer.classList.add('is-open');
    });

    this.drawerTogglers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));

    requestAnimationFrame(() => {
      const focusTarget = this.drawer.querySelector('[data-header-v2-drawer-close], button, [href]');
      if (focusTarget) focusTarget.focus();
    });

    this.drawer.addEventListener('keydown', this.trapFocusHandler);
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.classList.remove('is-drawer-open');
    this.drawer.classList.remove('is-open');

    setTimeout(() => {
      if (!this.classList.contains('is-drawer-open')) {
        this.drawer.hidden = true;
      }
    }, 320);

    this.drawerTogglers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

    document.body.classList.remove('is-header-v2-drawer-open');
    document.body.style.top = '';
    this.lastScrollY = this.bodyScrollY || 0;
    window.scrollTo(0, this.bodyScrollY || 0);

    this.showDrawerPanel('root');

    this.drawer.removeEventListener('keydown', this.trapFocusHandler);

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
    this.lastFocused = null;
  }

  onDrawerClose(e) {
    if (e) e.preventDefault();
    this.closeDrawer();
  }

  onDrawerDrill(e) {
    e.preventDefault();
    const target = e.currentTarget.dataset.drawerDrill;
    if (target) this.showDrawerPanel(target);
  }

  onDrawerBack(e) {
    e.preventDefault();
    const target = e.currentTarget.dataset.drawerBack;
    if (target) this.showDrawerPanel(target);
  }

  showDrawerPanel(panelId) {
    // Drawer was reparented to <body>; query panels from the drawer itself.
    const drawerScope = this.drawer || this;
    const targetPanel = drawerScope.querySelector(`[data-drawer-panel="${panelId}"]`);
    if (!targetPanel) return;

    const currentActiveId = this.activeDrawerPanel || 'root';
    if (currentActiveId === panelId && targetPanel.classList.contains('is-active')) return;

    const currentPanel = drawerScope.querySelector(`[data-drawer-panel="${currentActiveId}"]`);
    const targetLevel = parseInt(targetPanel.dataset.panelLevel || '1', 10);
    const currentLevel = currentPanel ? parseInt(currentPanel.dataset.panelLevel || '1', 10) : 1;
    const isForward = targetLevel > currentLevel;

    // When drilling forward, target may have stale `is-stacked-left` from a prior session.
    // Snap it back to the default off-screen-right position without animating.
    if (isForward && targetPanel.classList.contains('is-stacked-left')) {
      targetPanel.style.transition = 'none';
      targetPanel.classList.remove('is-stacked-left');
      targetPanel.hidden = true;
      void targetPanel.offsetHeight;
      targetPanel.style.transition = '';
    }

    // Bring target into DOM at its starting position, then paint before animating.
    targetPanel.hidden = false;
    void targetPanel.offsetHeight;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (currentPanel && currentPanel !== targetPanel) {
          currentPanel.classList.remove('is-active');
          if (isForward) {
            currentPanel.classList.add('is-stacked-left');
          } else {
            currentPanel.classList.remove('is-stacked-left');
          }
        }
        targetPanel.classList.remove('is-stacked-left');
        targetPanel.classList.add('is-active');
      });
    });

    this.activeDrawerPanel = panelId;

    setTimeout(() => {
      this.drawerPanels.forEach((panel) => {
        if (!panel.classList.contains('is-active') && !panel.classList.contains('is-stacked-left')) {
          panel.hidden = true;
        }
      });
    }, 500);

    setTimeout(() => {
      const focusTarget =
        targetPanel.querySelector('[data-drawer-back]') ||
        targetPanel.querySelector('[data-header-v2-drawer-close]') ||
        targetPanel.querySelector('button, [href]');
      if (focusTarget) focusTarget.focus();
    }, 320);
  }

  trapFocusHandler(e) {
    if (e.key !== 'Tab' || !this.drawer) return;
    const activePanel = this.drawer.querySelector('.header-v2__drawer-panel.is-active') || this.drawer;
    const focusable = activePanel.querySelectorAll(
      'button:not([disabled]):not([hidden]), a[href]:not([hidden]), input:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ===== Global listeners ===== */

  onDocClick(e) {
    if (this.activeMegaPanel == null || !this.megaContainer) return;
    const insideMega = this.megaContainer.contains(e.target);
    const onToggler = Array.from(this.megaTogglers).some((b) => b.contains(e.target));
    if (!insideMega && !onToggler) {
      this.closeMega();
    }
  }

  onDocKey(e) {
    if (e.key !== 'Escape') return;

    if (this.classList.contains('is-drawer-open')) {
      this.closeDrawer();
      return;
    }

    if (this.activeMegaPanel != null) {
      const toRestore = Array.from(this.megaTogglers).find(
        (b) => b.dataset.headerV2MegaToggle === this.activeMegaPanel
      );
      this.closeMega();
      if (toRestore) toRestore.focus();
    }
  }

  onBreakpointChange() {
    this.closeMega();
    this.closeDrawer();
  }

  updateCartCount(count) {
    if (!this.cartLinkEl) return;

    const itemCount = parseInt(count, 10) || 0;
    let countEl = this.cartLinkEl.querySelector('.header-v2__cart-count');

    if (itemCount > 0) {
      if (!countEl) {
        const wrap = this.cartLinkEl.querySelector('.header-v2__cart-icon-wrap');
        countEl = document.createElement('span');
        countEl.className = 'header-v2__cart-count';
        countEl.setAttribute('aria-hidden', 'true');
        (wrap || this.cartLinkEl).appendChild(countEl);
      }
      countEl.textContent = itemCount;
    } else if (countEl) {
      countEl.remove();
    }

    this.cartLinkEl.setAttribute('aria-label', 'Cart (' + itemCount + ' items)');
  }
}

if (!customElements.get('header-v2')) {
  customElements.define('header-v2', HeaderV2);
}
